import React, { useState, useEffect, useRef } from 'react';
import { api } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Upload, ArrowRight, Image, Layers } from 'lucide-react';

export default function TransformationsTab({ services }) {
  const [transformations, setTransformations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [beforePreview, setBeforePreview] = useState(null);
  const [afterPreview, setAfterPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [serviceId, setServiceId] = useState('');

  const beforeRef = useRef(null);
  const afterRef = useRef(null);

  useEffect(() => { fetchTransformations(); }, []);

  const fetchTransformations = async () => {
    try {
      const res = await api.get('/providers/me/transformations');
      setTransformations(res.data || []);
    } catch (err) { console.error('Failed to load transformations:', err); }
    finally { setLoading(false); }
  };

  const handleFileSelect = (file, type) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Only image files'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    const url = URL.createObjectURL(file);
    if (type === 'before') { setBeforeFile(file); setBeforePreview(url); }
    else { setAfterFile(file); setAfterPreview(url); }
  };

  const resetForm = () => {
    setBeforeFile(null); setAfterFile(null);
    setBeforePreview(null); setAfterPreview(null);
    setCaption(''); setServiceId(''); setShowForm(false);
  };

  const handleUpload = async () => {
    if (!beforeFile || !afterFile) { toast.error('Please select both before and after images'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('before_image', beforeFile);
      formData.append('after_image', afterFile);
      formData.append('caption', caption);
      formData.append('service_id', serviceId === 'none' ? '' : serviceId);
      const res = await api.post('/providers/me/transformations', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setTransformations(prev => [res.data, ...prev]);
      resetForm();
      toast.success('Transformation uploaded!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/providers/me/transformations/${id}`);
      setTransformations(prev => prev.filter(t => t.transformation_id !== id));
      toast.success('Transformation deleted');
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return (
    <div className="flex justify-center py-16">
      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#D4AF37]" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="transformations-tab">
      <div className="card-surface p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-medium text-[#1A1A1A] flex items-center gap-2">
            <Layers className="h-5 w-5 text-[#D4AF37]" />
            Before & After Transformations
          </h3>
          <Button variant="outline" onClick={() => setShowForm(!showForm)} className="text-sm" data-testid="add-transformation-btn">
            <Plus className="h-4 w-4 mr-1" />
            {showForm ? 'Cancel' : 'Add Transformation'}
          </Button>
        </div>
        <p className="text-sm text-stone-400 mb-4">Showcase your best work with before & after images. Link them to services for easy booking.</p>

        {/* Upload Form */}
        {showForm && (
          <div className="mb-6 p-5 bg-[#F9F6F0] rounded-lg space-y-4" data-testid="transformation-form">
            <div className="grid grid-cols-2 gap-4">
              {/* Before */}
              <div>
                <Label className="text-xs mb-1 block">Before</Label>
                <input ref={beforeRef} type="file" accept="image/*" className="hidden" onChange={e => handleFileSelect(e.target.files?.[0], 'before')} />
                <button
                  onClick={() => beforeRef.current?.click()}
                  className={`w-full aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center transition-colors overflow-hidden ${beforePreview ? 'border-[#D4AF37]' : 'border-stone-300 hover:border-[#D4AF37]'}`}
                  data-testid="before-image-upload"
                >
                  {beforePreview ? (
                    <img src={beforePreview} alt="Before" className="w-full h-full object-cover" />
                  ) : (
                    <>
                      <Upload className="h-6 w-6 text-stone-400 mb-1" />
                      <span className="text-xs text-stone-400">Upload Before</span>
                    </>
                  )}
                </button>
              </div>
              {/* After */}
              <div>
                <Label className="text-xs mb-1 block">After</Label>
                <input ref={afterRef} type="file" accept="image/*" className="hidden" onChange={e => handleFileSelect(e.target.files?.[0], 'after')} />
                <button
                  onClick={() => afterRef.current?.click()}
                  className={`w-full aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center transition-colors overflow-hidden ${afterPreview ? 'border-[#D4AF37]' : 'border-stone-300 hover:border-[#D4AF37]'}`}
                  data-testid="after-image-upload"
                >
                  {afterPreview ? (
                    <img src={afterPreview} alt="After" className="w-full h-full object-cover" />
                  ) : (
                    <>
                      <Upload className="h-6 w-6 text-stone-400 mb-1" />
                      <span className="text-xs text-stone-400">Upload After</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-xs">Caption (optional)</Label>
              <Input
                value={caption}
                onChange={e => setCaption(e.target.value)}
                placeholder="e.g. Box braids installation, 4 hours"
                className="input-zuriya"
                maxLength={120}
                data-testid="transformation-caption"
              />
            </div>

            <div>
              <Label className="text-xs">Link to Service (optional)</Label>
              <Select value={serviceId} onValueChange={setServiceId}>
                <SelectTrigger className="input-zuriya" data-testid="transformation-service-select">
                  <SelectValue placeholder="Choose a service..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {(services || []).map(s => (
                    <SelectItem key={s.service_id} value={s.service_id}>{s.name}, £{s.price}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button onClick={handleUpload} disabled={uploading || !beforeFile || !afterFile} className="btn-primary w-full" data-testid="upload-transformation-btn">
              {uploading ? 'Uploading...' : 'Upload Transformation'}
            </Button>
          </div>
        )}

        {/* List */}
        {transformations.length === 0 ? (
          <div className="text-center py-10" data-testid="transformations-empty">
            <Image className="h-12 w-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500 mb-1">No transformations yet</p>
            <p className="text-xs text-stone-400">Upload before & after images to showcase your best work</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="transformations-list">
            {transformations.map(t => (
              <div key={t.transformation_id} className="card-surface overflow-hidden group" data-testid={`transformation-${t.transformation_id}`}>
                <div className="flex">
                  <div className="w-1/2 aspect-square relative">
                    <img src={t.before_image} alt="Before" className="w-full h-full object-cover" />
                    <span className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded">Before</span>
                  </div>
                  <div className="w-1/2 aspect-square relative">
                    <img src={t.after_image} alt="After" className="w-full h-full object-cover" />
                    <span className="absolute top-2 right-2 bg-[#D4AF37] text-white text-xs px-2 py-0.5 rounded">After</span>
                  </div>
                </div>
                <div className="p-3 flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    {t.caption && <p className="text-sm text-[#1A1A1A] font-medium truncate">{t.caption}</p>}
                    {t.service_name && (
                      <p className="text-xs text-[#D4AF37]">Linked: {t.service_name}</p>
                    )}
                  </div>
                  <button onClick={() => handleDelete(t.transformation_id)} className="p-1.5 hover:bg-red-50 rounded" data-testid={`delete-transformation-${t.transformation_id}`}>
                    <Trash2 className="h-4 w-4 text-red-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
